import { useState } from 'react';

import Card from '@mui/material/Card';
import Grid from '@mui/system/Unstable_Grid';
import { Stack, Button } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { useGetProfileDocumentsQuery } from 'src/services/candidates/candidates.service';

import { useTable } from 'src/components/table';
import FileManagerTable from 'src/components/documents/file-manager-table';

import UploadCandidateDocumentModal from '../modals/upload-document-modal';

const DEFAULT_PAGE = {
  page: 1,
  per_page: 10,
};

type Props = {
  profileId?: string;
  uploadVisible?: boolean;
  onDelete?: (id: string) => void;
  onSaveToProfile?: (id: string) => void;
  /**
   * Show the per-document "share with guest" toggle.
   *
   * Opt-in, and only passed from the application views. The same view also renders a candidate's
   * own documents page and this table is reached from the candidate, company, contact and deal
   * records — guest sharing is meaningless there, and a toggle in those places would invite a
   * recruiter to think they had shared something.
   */
  guestSharing?: boolean;
};

export default function CandidateProfileDocumentsView({
  profileId,
  uploadVisible = true,
  onDelete,
  onSaveToProfile,
  guestSharing = false,
}: Props) {
  const [page, setPage] = useState(DEFAULT_PAGE);

  const showUploadModal = useBoolean();

  const { t } = useTranslate();

  const { currentData: myDocuments, isLoading } = useGetProfileDocumentsQuery(
    {
      profileId: profileId as string,
      params: page,
    },
    { skip: !profileId }
  );

  const table = useTable();

  const notFound = !myDocuments?.count && !isLoading;

  return (
    <Grid container spacing={3}>
      {
        uploadVisible && (
          <Grid xs={12}>
            <Stack direction="row" justifyContent="flex-end">
              <Button variant="contained" disabled={!profileId} onClick={showUploadModal.onTrue}>
                {t('profile.documents.upload')}
              </Button>
            </Stack>
          </Grid>
        )
      }
      <Grid xs={12}>
        <Card>
          <FileManagerTable
            table={table}
            dataFiltered={myDocuments?.results ?? []}
            count={myDocuments?.count ?? 0}
            // onDeleteRow={handleDeleteItem}
            notFound={notFound}
            documentType="candidate"
            showGuestSharing={guestSharing}
            onChangePage={(_e, pageNumber) => setPage({ ...page, page: pageNumber + 1 })}
            onChangePerPage={(e) => setPage({ ...page, per_page: Number(e.target.value) })}
            onDeleteRow={onDelete}
            onSaveToProfile={onSaveToProfile}
            pageOptions={page}
          />
        </Card>
      </Grid>

      {profileId && (
        <UploadCandidateDocumentModal
          open={showUploadModal.value}
          onClose={showUploadModal.onFalse}
          profileId={profileId}
        />
      )}
    </Grid>
  );
}
